import { Link } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { FIXED_SECTIONS, HOME_SECTION, SECTIONS, type NavSection } from '../../config/navigation';
import { sectionOn } from '../../config/navigationModules';
import { useMemberScope } from '../../hooks/useMemberScope';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useSwitchedOffModules } from '../../hooks/useSwitchedOffModules';
import { useActiveSection } from '../../hooks/useActiveSection';
import { usePinnedTools } from '../../hooks/usePinnedTools';
import { useRelationsUnreadCount } from '../../hooks/useRelationsUnreadCount';
import { useHeldMails } from '../../hooks/useHeldMails';
import { useWaitingSignal } from '../../hooks/useWaitingSignal';
import { useWindowControlsOverlay } from '../../hooks/useWindowControlsOverlay';
import { ADMIN_HOME } from '../../lib/access/screenAccess';
import { useCanUseTelephony } from '../../lib/telephonyAccess';
import { OttoNow } from '../bureau/OttoNow';
import { NotificationBell } from '../notifications/NotificationBell';
import { DialerMenu } from '../telephony/DialerMenu';
import { AdminLogo } from '../ui/AdminLogo';
import { CountPop } from '../ui/CountPop';
import { ToolsMenu } from './ToolsMenu';
import { UserDropdown } from './UserDropdown';
import { WorkshopScreenDock } from './WorkshopScreenDock';

interface HeaderBarProps {
  onOpenSearch: () => void;
  /** Ouvre le drawer de navigation mobile (< lg). */
  onOpenMobileNav?: () => void;
}

/**
 * The header of the Bureau (mock-up « Navigation », PLAN-BUREAU lot 3): the Bureau first, the
 * tools Gilles pinned (Relations by default), the « Outils » menu for the others — the tool open
 * right now shows as a tab too, so the page is always named —, then the small Otto (what he does
 * now), the search, the phone and the notifications, and the avatar. Two counts, two natures:
 * Relations = mails to answer (blue, volume); Bureau = what waits on Gilles (amber, the colour of
 * waiting everywhere). The Otto chat button left with the mock-up; since 12/10/2026 the Bureau no
 * longer opens the chat at all: talking to Otto is a mission (« Nouvelle mission » in the small Otto).
 */
export function HeaderBar(props: HeaderBarProps) {
  const canOpen = useCanOpen();
  // The Bureau's signals (what waits, the held mails, the mails to answer, what Otto does, the workshop's screen) are
  // read from the missions and the mailboxes: only for whoever opens the Bureau (lot L6g), never polled for the others.
  return canOpen(ADMIN_HOME) ? <HeaderBarWithSignals {...props} /> : <HeaderBarView {...props} relationsUnread={0} waiting={0} bureau={false} />;
}

function HeaderBarWithSignals(props: HeaderBarProps) {
  const relationsUnread = useRelationsUnreadCount();
  // What waits on Gilles, as the Bureau's « T'attend » counts it: the server's lines and the held mails.
  const waiting = (useWaitingSignal().count ?? 0) + useHeldMails().length;
  return <HeaderBarView {...props} relationsUnread={relationsUnread} waiting={waiting} bureau />;
}

interface HeaderBarViewProps extends HeaderBarProps {
  readonly relationsUnread: number;
  readonly waiting: number;
  /** The Bureau's own widgets: the small Otto and the workshop's screen. */
  readonly bureau: boolean;
}

function HeaderBarView({ onOpenSearch, onOpenMobileNav, relationsUnread, waiting, bureau }: HeaderBarViewProps) {
  const activeSection = useActiveSection();
  const wcoActive = useWindowControlsOverlay();
  const canTelephony = useCanUseTelephony();
  const { pinned, toggle } = usePinnedTools();

  const shown: NavSection[] = [...FIXED_SECTIONS, ...pinned.filter((id) => !FIXED_SECTIONS.includes(id))];
  if (activeSection !== 'settings' && !shown.includes(activeSection)) shown.push(activeSection);
  const off = useSwitchedOffModules();
  const scope = useMemberScope();
  const canOpen = useCanOpen();
  const tabs = shown.filter((id) => sectionOn(id, off, scope, canOpen)).map((id) => SECTIONS.find((s) => s.id === id)).filter((s) => s !== undefined);

  return (
    <header
      className={twMerge(
        'wco-titlebar flex h-14 flex-shrink-0 items-center border-b border-desk-line bg-white',
        wcoActive && 'wco-drag-region',
      )}
    >
      {/* Hamburger — mobile only (< lg) */}
      <button
        onClick={onOpenMobileNav}
        className="ml-1.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-100 lg:hidden"
        aria-label="Ouvrir la navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <Link to="/admin" className="flex flex-shrink-0 items-center space-x-2.5 px-3 lg:pl-5 lg:pr-4" aria-label="Bureau">
        <AdminLogo size={30} />
        <span className="hidden text-[17px] font-bold tracking-tight text-brand-500 lg:inline">AutoQuote</span>
      </Link>

      {/* Tabs — desktop only; they shrink and scroll inside rather than push the whole layout. */}
      <nav aria-label="Principal" className="hidden h-full min-w-0 items-stretch gap-0.5 overflow-x-auto lg:flex">
        {tabs.map((section) => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          const isHome = section.id === HOME_SECTION;
          const badge = section.id === 'relations' ? relationsUnread : isHome ? waiting : 0;
          return (
            <Link
              key={section.id}
              to={section.href}
              aria-current={isActive ? 'page' : undefined}
              className={twMerge(
                'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 transition-colors',
                isHome ? 'text-sm' : 'text-[13.5px]',
                isActive
                  ? 'border-brand-500 font-semibold text-desk-ink'
                  : 'border-transparent font-medium text-desk-soft hover:border-desk-ring hover:text-desk-ink',
              )}
            >
              <Icon className={twMerge('h-4 w-4', isActive ? 'text-brand-500' : 'text-desk-muted')} />
              {section.label}
              {badge > 0 && (
                <span
                  className={twMerge(
                    'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-bold text-white',
                    isHome ? 'bg-amber-700' : 'bg-brand-500',
                  )}
                  title={isHome ? "Ce qui t'attend (hors sujets reportés)" : 'Mails à répondre'}
                >
                  <CountPop value={badge > 99 ? '99+' : badge} />
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="hidden lg:block">
        <ToolsMenu active={activeSection === 'settings' ? null : activeSection} pinned={pinned} onTogglePin={toggle} counts={{ relations: relationsUnread }} />
      </div>

      <div className="min-w-0 flex-1" />

      <div className="flex flex-shrink-0 items-center gap-1 px-2 sm:gap-1.5 sm:pr-4">
        {bureau && <OttoNow />}
        <button
          onClick={onOpenSearch}
          className="hidden h-8 w-[260px] items-center gap-2 rounded-lg border border-desk-line bg-desk-subtle px-2.5 text-[13px] text-desk-muted transition-colors hover:border-desk-ring hover:bg-desk-line/60 md:flex"
        >
          <Search className="h-[15px] w-[15px] flex-shrink-0" strokeWidth={1.75} />
          <span className="flex-1 truncate text-left">Rechercher, ouvrir un outil…</span>
          <kbd className="hidden flex-shrink-0 rounded-[5px] border border-desk-ring bg-white px-[5px] text-[11px] leading-4 lg:inline-block">Ctrl K</kbd>
        </button>
        <button
          onClick={onOpenSearch}
          className="flex items-center justify-center rounded-lg p-2 text-desk-soft hover:bg-desk-subtle hover:text-desk-ink md:hidden"
          aria-label="Rechercher"
        >
          <Search className="h-4 w-4" />
        </button>
        {canTelephony && <DialerMenu />}
        <NotificationBell />
        {/* Écran de l'atelier : le navigateur de la machine, à un clic (PLAN-ATELIER-OTTO § 16.6) */}
        {bureau && <WorkshopScreenDock />}
        <UserDropdown />
      </div>
    </header>
  );
}
